import type { TransactionHistoryRow } from '@pf/contracts';
import { useNavigate } from '@tanstack/react-router';
import { Archive, Search } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useTransactionHistory } from '@/lib/api/ops-queries';
import { formatDateTime } from '@/lib/datetime';
import { readParam, useListState } from '@/lib/nav/list-state';
import { HISTORY_LIST, HISTORY_SORTS, HISTORY_STATUSES } from '@/lib/nav/ops-lists';
import { ALL_WEBS, useWebScope } from '@/lib/scope/web-scope';
import { cn, shortId } from '@/lib/utils';
import { CopyButton } from '@/components/domain/copy-button';
import { ParticipantPair } from '@/components/domain/participant-chip';
import { WebScopeScopedNotice } from '@/components/domain/web-scope-select';
import { MoneyDisplay } from '@/components/domain/money-display';
import { StateBadge } from '@/components/domain/state-badge';
import { DataTable, type Column } from '@/components/layout/data-table';
import { PageHeader } from '@/components/layout/page';
import { Pager } from '@/components/layout/pager';
import { SortControl } from '@/components/layout/sort-control';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/ui/feedback';
import { Input, Label } from '@/components/ui/primitives';

/** A sort key from the table header or the mobile control, ignored unless History offers it. */
const readSort = readParam.oneOf(HISTORY_SORTS);

/**
 * Who let go of an `EXPIRED` deposit, or null when nobody did — the timer fired, or the row
 * predates the cause being recorded.
 *
 * Two of the three causes are somebody's decision and read as a cancel to the person it happened
 * to: `PARTICIPANT_RELEASE` is the depositor handing the slot back, `OPERATOR_CANCELLED` is an
 * admin cancelling the matched withdrawal out from under an unpaid reservation, which expires
 * this deposit in the same commit. `RESERVATION_TTL` is the one that really is "ran out of
 * time", and it keeps the plain expired badge.
 */
function cancelledBy(
  row: Pick<TransactionHistoryRow, 'status' | 'expired_cause'>,
): 'PLAYER' | 'OPERATOR' | null {
  if (row.status !== 'EXPIRED') return null;
  if (row.expired_cause === 'PARTICIPANT_RELEASE') return 'PLAYER';
  if (row.expired_cause === 'OPERATOR_CANCELLED') return 'OPERATOR';
  return null;
}

/**
 * The status a row *renders* as. An `EXPIRED` row somebody cancelled shows as `CANCELLED` — that
 * is what happened to the customer, and "expired" was only the state machine's name for the
 * shared arrow (see `DEPOSIT_DISPLAY_STATE_META`). A row without a cause (pre-split events,
 * older API) keeps the plain expired badge.
 */
function displayStatus(row: TransactionHistoryRow): string {
  return cancelledBy(row) === null ? row.status : 'CANCELLED';
}

/**
 * A copy control inside a clickable row: the click must copy, never navigate.
 *
 * The whole row is a link to the detail page, so without the stop the icon would do both at
 * once — copy the id and yank the operator off the list they were scanning.
 */
function RowCopy({ value, label }: { value: string; label: string }): ReactNode {
  return (
    <span
      className="inline-flex shrink-0"
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      <CopyButton value={value} label={label} className="size-5 [&_svg]:size-3" />
    </span>
  );
}

/**
 * One customer's handle: the website's username when it has sent one, else the opaque
 * `account_ref`, else an em dash.
 *
 * The shown value is what History's search box matches whole — either kind — so it carries a
 * copy control like the ids do. Monospaced only for a raw ref: a username is a name and reads
 * as one, while a ref is a token whose characters have to be compared.
 */
function AccountHandle({
  label,
  display,
  accountRef,
  copyLabel,
}: {
  label: string;
  display: string | null | undefined;
  accountRef: string | null | undefined;
  copyLabel: string;
}): ReactNode {
  const shown = display ?? accountRef;
  return (
    <p className="flex min-w-0 items-center gap-1">
      <span className="shrink-0 text-2xs text-muted-foreground">{label}</span>
      {shown == null ? (
        <span className="text-2xs text-muted-foreground">—</span>
      ) : (
        <>
          <span className={display != null ? 'truncate text-xs' : 'truncate font-mono text-2xs'}>
            {shown}
          </span>
          <RowCopy value={shown} label={copyLabel} />
        </>
      )}
    </p>
  );
}

/**
 * History — every deposit that has finished, newest first.
 *
 * Deliberately a rear-view mirror: only terminal rows, offset pagination, no polling. Money
 * still moving lives on the dashboard and the review queue; this page answers "what happened",
 * usually for a support question that arrives days later.
 *
 * The filter, sort and page live in the URL (`@/lib/nav/list-state`): a support question is
 * answered by a search someone else needs to see, and a shareable link is the cheapest way to
 * hand it over.
 */
export function HistoryPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { scope } = useWebScope();
  const view = useListState(HISTORY_LIST);
  const { status, q: search, amount, sort, page, limit } = view.state;
  const dir = view.state.dir ?? 'DESC';

  const history = useTransactionHistory({ status, search, amount, sort, dir, page, limit });

  // Header click: same column flips the direction, a new column starts newest/largest-first.
  const sortBy = (key: string): void => {
    const next = readSort(key);
    if (next === undefined) return;
    view.set(
      next === sort
        ? { dir: dir === 'ASC' ? 'DESC' : 'ASC', page: 0 }
        : { sort: next, dir: undefined, page: 0 },
    );
  };
  const rows = history.data?.items ?? [];
  const total = history.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / limit));

  const columns: Column<TransactionHistoryRow>[] = [
    {
      id: 'transaction',
      header: t('opsHistory.columns.transaction'),
      priority: 'primary',
      cell: (row) => (
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs font-medium">{shortId(row.id)}</span>
            <RowCopy value={row.id} label={t('opsHistory.copyLabels.transactionId')} />
            <StateBadge machine="deposit" status={displayStatus(row)} size="sm" />
            {cancelledBy(row) !== null ? (
              // Who cancelled — the depositor releasing their own reservation, or an admin
              // cancelling the withdrawal it was matched to. An admin *resolving* a deposit is
              // a different thing entirely: that goes through review and stamps the operator
              // column, and never lands here because it never expires the row.
              <span className="text-2xs text-muted-foreground">
                {t(`states.cancelledBy.${cancelledBy(row) ?? 'PLAYER'}`)}
              </span>
            ) : null}
          </div>
          <ParticipantPair depositor={row.participant} withdrawer={row.withdrawal_participant} />
        </div>
      ),
    },
    {
      id: 'amount',
      header: t('opsHistory.columns.amount'),
      align: 'right',
      priority: 'primary',
      sortKey: 'AMOUNT',
      cell: (row) => <MoneyDisplay amount={row.amount} />,
    },
    {
      id: 'parties',
      header: t('opsHistory.columns.parties'),
      priority: 'primary',
      cell: (row) => (
        <div className="min-w-0 space-y-0.5 text-xs">
          <p className="flex min-w-0 items-center gap-1">
            <span className="shrink-0 text-2xs text-muted-foreground">
              {t('opsHistory.partyLabels.depositor')}
            </span>
            <span className="truncate">{row.depositor_name ?? '—'}</span>
            {row.depositor_name ? (
              <RowCopy
                value={row.depositor_name}
                label={t('opsHistory.copyLabels.depositorName')}
              />
            ) : null}
          </p>
          <p className="flex min-w-0 items-center gap-1">
            <span className="shrink-0 text-2xs text-muted-foreground">
              {t('opsHistory.partyLabels.withdrawer')}
            </span>
            <span className="truncate">{row.withdrawer_name ?? '—'}</span>
            {row.withdrawer_name ? (
              <RowCopy
                value={row.withdrawer_name}
                label={t('opsHistory.copyLabels.withdrawerName')}
              />
            ) : null}
          </p>
        </div>
      ),
    },
    {
      id: 'username',
      header: t('opsHistory.columns.username'),
      priority: 'secondary',
      // Both sides stacked, mirroring the parties column beside it — the two customers are read
      // together, and splitting them across two columns doubled the width for the same fact.
      cell: (row) => (
        <div className="min-w-0 space-y-0.5">
          <AccountHandle
            label={t('opsHistory.partyLabels.depositor')}
            display={row.depositor_account_display}
            accountRef={row.depositor_account_ref}
            copyLabel={t('opsHistory.copyLabels.username')}
          />
          <AccountHandle
            label={t('opsHistory.partyLabels.withdrawer')}
            display={row.withdrawer_account_display}
            accountRef={row.withdrawer_account_ref}
            copyLabel={t('opsHistory.copyLabels.withdrawerUsername')}
          />
        </div>
      ),
    },
    {
      id: 'operator',
      header: t('opsHistory.columns.operator'),
      priority: 'secondary',
      cell: (row) =>
        row.operator_name == null ? (
          <span className="text-2xs text-muted-foreground">{t('opsHistory.automatic')}</span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs">
            <span className="truncate">{row.operator_name}</span>
            <RowCopy value={row.operator_name} label={t('opsHistory.copyLabels.operatorName')} />
          </span>
        ),
    },
    {
      id: 'withdrawal',
      header: t('opsHistory.columns.matchedWithdrawal'),
      priority: 'secondary',
      cell: (row) =>
        row.matched_withdrawal_id === null ? (
          <span className="text-2xs text-muted-foreground">—</span>
        ) : (
          <span className="inline-flex items-center gap-1">
            <span className="font-mono text-2xs">{shortId(row.matched_withdrawal_id)}</span>
            <RowCopy
              value={row.matched_withdrawal_id}
              label={t('opsHistory.copyLabels.withdrawalId')}
            />
          </span>
        ),
    },
    {
      id: 'finished',
      header: t('opsHistory.columns.finishedAt'),
      priority: 'secondary',
      sortKey: 'FINISHED',
      cell: (row) => (
        <span className="text-xs tabular-nums text-muted-foreground">
          {formatDateTime(row.updated_at)}
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader title={t('opsHistory.title')} description={t('opsHistory.description')} />

      {/* A support question answered from the whole pool is History's normal state, so this is the
          one signal that a shared link — or the total on screen — describes one site rather than
          every deposit that finished. */}
      <WebScopeScopedNotice className="mb-4" />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {HISTORY_STATUSES.map((candidate) => (
            <button
              key={candidate}
              type="button"
              aria-pressed={status === candidate}
              onClick={() => {
                view.set({ status: candidate, page: 0 });
              }}
              className={cn(
                'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                status === candidate
                  ? 'border-primary bg-accent text-accent-foreground'
                  : 'border-border bg-card text-muted-foreground hover:text-foreground',
              )}
            >
              {t(`opsHistory.statuses.${candidate}`)}
            </button>
          ))}
        </div>
        <div className="ml-auto flex flex-wrap items-end gap-3">
          <div className="w-full max-w-xs space-y-1">
            <Label htmlFor="history-search" className="sr-only">
              {t('opsHistory.searchLabel')}
            </Label>
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                id="history-search"
                className="pl-8"
                placeholder={t('opsHistory.searchPlaceholder')}
                value={search}
                onChange={(event) => {
                  view.set({ q: event.target.value, page: 0 });
                }}
              />
            </div>
          </div>
          <div className="w-full max-w-40 space-y-1">
            <Label htmlFor="history-amount" className="sr-only">
              {t('opsHistory.amountLabel')}
            </Label>
            <Input
              id="history-amount"
              inputMode="numeric"
              className="tnum"
              placeholder={t('opsHistory.amountPlaceholder')}
              value={amount}
              onChange={(event) => {
                view.set({ amount: event.target.value.replace(/[^\d.]/g, ''), page: 0 });
              }}
            />
          </div>
          <SortControl
            id="history-sort"
            className="md:hidden"
            label={t('opsHistory.sortLabel')}
            options={[
              { value: 'FINISHED', label: t('opsHistory.sortOptions.FINISHED') },
              { value: 'AMOUNT', label: t('opsHistory.sortOptions.AMOUNT') },
            ]}
            value={sort}
            dir={dir}
            // Changing the column here keeps the direction the operator chose beside it.
            onValueChange={(value) => {
              const next = readSort(value);
              if (next === undefined) return;
              view.set({ sort: next, dir, page: 0 });
            }}
            onDirChange={(value) => {
              view.set({ dir: value, page: 0 });
            }}
          />
        </div>
      </div>

      {history.isLoading ? (
        <TableSkeleton rows={8} />
      ) : history.isError ? (
        <ErrorState
          error={history.error}
          onRetry={() => {
            void history.refetch();
          }}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Archive className="size-5" aria-hidden="true" />}
          title={
            search === '' && amount === '' && scope !== ALL_WEBS
              ? t('common.webScope.emptyTitle')
              : t('opsHistory.emptyTitle')
          }
          // A search that found nothing is the one case where the operator needs to be told the
          // rule: the box takes a transaction-id *fragment*, but an account reference is matched
          // whole (see `transactionHistory` in the metrics repository for why). Half a ref looks
          // like a typo-free search that simply has no results, and the empty state is the only
          // moment the distinction is worth spending words on — so the search explanation still
          // wins over the scope one when there is a search to explain.
          description={
            search !== ''
              ? t('opsHistory.emptySearchBody')
              : scope !== ALL_WEBS
                ? t('common.webScope.emptyDescription', { web: scope })
                : t('opsHistory.emptyBody')
          }
        />
      ) : (
        <div className="space-y-3">
          <DataTable
            caption={t('opsHistory.title')}
            columns={columns}
            rows={rows}
            rowKey={(row) => row.id}
            sort={{ key: sort, dir, onChange: sortBy }}
            onRowClick={(row) => {
              // `from` is this exact view: the row's back link returns to the same filter and
              // page, not to an unfiltered first page the operator then has to redo.
              void navigate({
                to: '/ops/history/$transactionId',
                params: { transactionId: row.id },
                search: { from: view.href },
              });
            }}
          />
          <Pager
            summary={t('opsHistory.pageSummary', { page: page + 1, pages, total })}
            page={page}
            pages={pages}
            limit={limit}
            onPageChange={(value) => {
              view.set({ page: value });
            }}
            onLimitChange={(value) => {
              view.set({ limit: value, page: 0 });
            }}
          />
        </div>
      )}
    </>
  );
}
